import {
  createContext, useContext, useEffect, useState, useCallback, useRef,
} from "react";
import axios from "axios";

export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Always send credentials so cookies flow.
axios.defaults.withCredentials = true;

const AuthContext = createContext(null);

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

// ── module-scope refresh coordination ─────────────────────────────
// The axios interceptor may fire dozens of times in parallel during a
// 600-file upload burst; we must only ever have ONE /auth/refresh call
// in flight at a time so we don't rotate the refresh cookie under
// ourselves. All callers wait on the same promise.
let refreshInFlight = null;
async function requestFreshAccessToken() {
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = axios.post(`${API}/auth/refresh`)
    .then((r) => r.data)
    .finally(() => { refreshInFlight = null; });
  return refreshInFlight;
}

// ── one-shot axios interceptor install (module load) ──────────────
// On any 401 response (except from /auth/* itself), transparently call
// /auth/refresh once and replay the original request. This is what
// makes multi-hour uploads survive the 15-min access-token expiry.
let interceptorInstalled = false;
function installAxiosInterceptor(onUnauth) {
  if (interceptorInstalled) return;
  interceptorInstalled = true;
  axios.interceptors.response.use(
    (r) => r,
    async (error) => {
      const cfg = error?.config || {};
      const status = error?.response?.status;
      const url = String(cfg.url || "");
      const isAuthRoute = url.includes("/auth/login")
        || url.includes("/auth/refresh")
        || url.includes("/auth/logout");
      if (status !== 401 || isAuthRoute || cfg._retriedAfterRefresh) {
        return Promise.reject(error);
      }
      try {
        await requestFreshAccessToken();
      } catch (refreshErr) {
        // Refresh itself failed → user is really logged out.
        onUnauth && onUnauth();
        return Promise.reject(error);
      }
      cfg._retriedAfterRefresh = true;
      return axios(cfg);
    },
  );
}

export function AuthProvider({ children }) {
  // user states: null (checking), false (not authed), {…} (authed)
  const [user, setUser] = useState(null);
  const heartbeatRef = useRef(null);

  const refresh = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/auth/me`);
      setUser(data);
    } catch {
      setUser(false);
    }
  }, []);

  // Install the interceptor exactly once. onUnauth flips the user state
  // to `false` which sends the ProtectedRoute to /login.
  useEffect(() => {
    installAxiosInterceptor(() => setUser(false));
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Heartbeat: while a user is authed, silently mint a fresh access
  // token every 10 minutes. Belt-and-suspenders on top of the 401
  // interceptor — a very long upload could keep the interceptor idle
  // for hours; the heartbeat guarantees the access cookie never
  // actually expires as long as the tab is open.
  useEffect(() => {
    if (heartbeatRef.current) {
      clearInterval(heartbeatRef.current);
      heartbeatRef.current = null;
    }
    if (user && user.id) {
      heartbeatRef.current = setInterval(() => {
        requestFreshAccessToken().catch(() => { /* swallow — interceptor picks up next 401 */ });
      }, 10 * 60 * 1000);
    }
    return () => {
      if (heartbeatRef.current) clearInterval(heartbeatRef.current);
    };
  }, [user]);

  const login = async (email, password) => {
    // Extended timeout so a cold-start production container (which can
    // take 30-60s to wake up) has room to answer before we bail. The
    // LoginPage's retry loop also handles genuine transport failures
    // with a "Waking up server…" UX.
    const { data } = await axios.post(
      `${API}/auth/login`, { email, password },
      { timeout: 60_000 },
    );
    setUser(data);
    return data;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch {
      // ignore — clear anyway
    }
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, refresh, setUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
